import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import analyticsFiltersApi from "../api/analytics/analyticsFiltersApi";
import { dateRangeForShortcut } from "../config/analytics/StaticDateRanges";
import { AnalyticsDateShortcut } from "../types/analytics/AnalyticsSavedFilter";
import { IDateRange } from "../types/generic";

export interface UseAnalyticsActivityWindowResolveArgs {
  /** Staged date intent — resolve only while All activity is staged. */
  stagedDateShortcut: AnalyticsDateShortcut;
  /** When true, resolve across every journey key available on the page. */
  allJourneys: boolean;
  journeyKeys: string[] | null | undefined;
  availableJourneyKeys: string[];
  onDateRangeChange(range: IDateRange | null): void;
}

/**
 * Decide whether All-activity resolve should run for the staged shortcut.
 * Chrome tab browsing does not affect this — only the staged shortcut does.
 */
export function activityWindowResolveAction(
  stagedDateShortcut: AnalyticsDateShortcut,
  signature: string,
  lastResolvedSignature: string | null,
): "resolve" | "idle" | "left-fit-shortcut" {
  if (stagedDateShortcut !== "FIT_TO_JOURNEYS") return "left-fit-shortcut";
  if (lastResolvedSignature === signature) return "idle";
  return "resolve";
}

export function activityWindowResolveSignature(
  allJourneys: boolean,
  journeyKeysKey: string,
  availableJourneyKeysKey: string,
): string {
  return allJourneys
    ? `all:${availableJourneyKeysKey}`
    : `sel:${journeyKeysKey}`;
}

export type ActivityWindowResolveKind = "range" | "empty" | "aborted" | "error";

/** Successful HTTP body: a window, or empty (no activity). Failures are not this path. */
export function activityWindowOutcomeFromResponse(
  window: { startDate?: string | null; endDate?: string | null } | null | undefined,
): Extract<ActivityWindowResolveKind, "range" | "empty"> {
  if (window?.startDate && window?.endDate) return "range";
  return "empty";
}

export function activityWindowOutcomeFromError(
  err: { aborted?: boolean } | null | undefined,
): Extract<ActivityWindowResolveKind, "aborted" | "error"> {
  if (err?.aborted) return "aborted";
  return "error";
}

/** Cache the journey signature only after a successful response (window or empty). */
export function shouldCacheActivityWindowSignature(
  kind: ActivityWindowResolveKind,
): boolean {
  return kind === "range" || kind === "empty";
}

/**
 * ADR-0003: while All activity (FIT_TO_JOURNEYS) is staged, keep an absolute
 * dateRange in sync with JourneyLogs ∪ AnswerSet activity for the current
 * journey selection. Audience facets are ignored (client-wide). Empty
 * resolve falls back to Last 12 months and surfaces emptyActivityHint.
 * Transport or server failures stay distinct (error + retry, no signature cache).
 *
 * onDateRangeChange is read via a ref so an unstable parent callback (typical
 * inline handler) cannot re-fire this effect every render and loop setState.
 */
export function useAnalyticsActivityWindowResolve({
  stagedDateShortcut,
  allJourneys,
  journeyKeys,
  availableJourneyKeys,
  onDateRangeChange,
}: UseAnalyticsActivityWindowResolveArgs): {
  emptyActivityHint: boolean;
  activityWindowError: boolean;
  retryActivityWindow(): void;
} {
  const auth = useAuth();
  const filtersApi = useMemo(
    () => new analyticsFiltersApi(auth.user?.access_token),
    [auth.user?.access_token],
  );

  const [emptyActivityHint, setEmptyActivityHint] = useState(false);
  const [activityWindowError, setActivityWindowError] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  // Drop stale responses when journey selection changes mid-flight.
  const resolveGenerationRef = useRef(0);
  /** Last journey selection we successfully applied a FIT window for. */
  const lastResolvedSignatureRef = useRef<string | null>(null);
  const onDateRangeChangeRef = useRef(onDateRangeChange);
  onDateRangeChangeRef.current = onDateRangeChange;

  // Primitive deps so a new [] / same-contents array from the parent does not re-resolve.
  const journeyKeysKey = (journeyKeys ?? []).join("|");
  const availableJourneyKeysKey = availableJourneyKeys.join("|");

  useEffect(() => {
    const signature = activityWindowResolveSignature(
      allJourneys,
      journeyKeysKey,
      availableJourneyKeysKey,
    );
    const action = activityWindowResolveAction(
      stagedDateShortcut,
      signature,
      lastResolvedSignatureRef.current,
    );

    if (action === "left-fit-shortcut") {
      setEmptyActivityHint(false);
      setActivityWindowError(false);
      lastResolvedSignatureRef.current = null;
      resolveGenerationRef.current += 1;
      return;
    }
    if (action === "idle") {
      return;
    }

    const keys = allJourneys
      ? availableJourneyKeysKey
        ? availableJourneyKeysKey.split("|")
        : []
      : journeyKeysKey
        ? journeyKeysKey.split("|")
        : [];
    const generation = ++resolveGenerationRef.current;
    const fallback = dateRangeForShortcut("LAST_12_MONTHS");

    const applySuccess = (
      kind: Extract<ActivityWindowResolveKind, "range" | "empty">,
      range: IDateRange | null,
    ) => {
      if (generation !== resolveGenerationRef.current) return;
      if (shouldCacheActivityWindowSignature(kind)) {
        lastResolvedSignatureRef.current = signature;
      }
      if (range) {
        onDateRangeChangeRef.current(range);
      }
      setEmptyActivityHint(kind === "empty");
      setActivityWindowError(false);
    };

    setActivityWindowError(false);

    if (!keys.length) {
      applySuccess("empty", fallback);
      return;
    }

    filtersApi
      .resolveActivityWindow(keys)
      .then((window) => {
        const kind = activityWindowOutcomeFromResponse(window);
        if (kind === "range" && window?.startDate && window?.endDate) {
          applySuccess("range", {
            startDate: new Date(window.startDate),
            endDate: new Date(window.endDate),
          });
        } else {
          applySuccess("empty", fallback);
        }
      })
      .catch((err) => {
        const kind = activityWindowOutcomeFromError(err);
        if (generation !== resolveGenerationRef.current) return;
        if (kind === "aborted") return;
        setEmptyActivityHint(false);
        setActivityWindowError(true);
      });

    return () => {
      // Discard in-flight responses when journeys/shortcut change or the effect re-runs.
      resolveGenerationRef.current += 1;
    };
  }, [
    stagedDateShortcut,
    allJourneys,
    journeyKeysKey,
    availableJourneyKeysKey,
    filtersApi,
    retryNonce,
  ]);

  const retryActivityWindow = () => {
    lastResolvedSignatureRef.current = null;
    setActivityWindowError(false);
    setRetryNonce((n) => n + 1);
  };

  return { emptyActivityHint, activityWindowError, retryActivityWindow };
}

export default useAnalyticsActivityWindowResolve;
